import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import Notification from '../models/Notification';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const NotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  refresh: () => {},
  markRead: () => {},
});

export const useNotifications = () => useContext(NotificationsContext);

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isRefreshingRef = useRef(false);

  const userId = user && user.id ? parseInt(user.id) : null;
  const companyId = user
    ? parseInt(user.company_id != null ? user.company_id : user.company?.id)
    : null;

  const unreadCount = notifications.filter(n => !parseInt(n.is_read)).length;

  const refresh = async () => {
    if (!userId || isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    setIsLoading(true);
    try {
      const mine = await Notification.mine(userId, isNaN(companyId) ? null : companyId);
      setNotifications(mine);
    } catch (error) {
      console.error('NotificationsContext.refresh error:', error);
    } finally {
      isRefreshingRef.current = false;
      setIsLoading(false);
    }
  };

  const markRead = async (uuid) => {
    const target = notifications.find(n => n.uuid === uuid);
    if (!target || parseInt(target.is_read)) return;
    // Optimistic: badge drops immediately; server call is idempotent
    setNotifications(prev => prev.map(n => (n.uuid === uuid ? { ...n, is_read: 1 } : n)));
    try {
      await Notification.markRead(uuid, userId);
    } catch (error) {
      console.error('NotificationsContext.markRead error:', error);
    }
  };

  // Fetch on login, clear on logout
  useEffect(() => {
    if (userId) {
      refresh();
    } else {
      setNotifications([]);
    }
  }, [userId]);

  // Refresh when the browser tab regains visibility (web analog of app-foreground)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [userId, companyId]);

  // Periodic refresh while the tab is open
  useEffect(() => {
    if (!userId) return;
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userId, companyId]);

  return (
    <NotificationsContext.Provider value={{ notifications, unreadCount, isLoading, refresh, markRead }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsContext;
